import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, LogOut, Upload, FileText, Send, Clock, CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { NotificationBell } from '@/components/portal/NotificationBell';

const DOC_TYPES = ['Business license', 'Food establishment license', 'USDA inspection', 'Dairy license', 'Organic certification', 'Cottage food documentation', 'Insurance certificate', 'W-9', 'Product labels', 'Product photographs', 'Farm photographs', 'Wholesale price sheet', 'Other'];

const STATUS_META = {
  'Submitted': { icon: Clock, color: 'text-gold', bg: 'bg-gold/10', msg: "Thank you for applying to become a FarmBox Partner! Destini will review your application and contact you to learn more about your farm and discuss whether FarmBox is a good fit. There is no obligation to participate by submitting an application." },
  'Under Review': { icon: Clock, color: 'text-gold', bg: 'bg-gold/10', msg: 'The FarmBox team is currently reviewing your application.' },
  'More Information Needed': { icon: AlertCircle, color: 'text-gold-dark', bg: 'bg-gold/15', msg: 'FarmBox needs more information. Please review notes, update, and re-submit.' },
  'Waitlisted': { icon: Clock, color: 'text-brown', bg: 'bg-brown/10', msg: 'You have been waitlisted. We will reach out as capacity opens up.' },
  'Declined': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', msg: 'Unfortunately your application was not approved at this time.' },
  'Suspended': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', msg: 'Your partnership is currently suspended. Please contact FarmBox.' },
  'Draft': { icon: Clock, color: 'text-dark/60', bg: 'bg-dark/5', msg: 'Your application is a draft.' },
  'Approved': { icon: CheckCircle2, color: 'text-forest', bg: 'bg-forest/10', msg: "You're approved! Head to your Farm Partner Portal to get started." },
  'Approved With Conditions': { icon: CheckCircle2, color: 'text-forest', bg: 'bg-forest/10', msg: "You're approved! Head to your Farm Partner Portal to get started." },
};

export default function ApplicationStatus() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [farm, setFarm] = useState(null);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [uploading, setUploading] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [tab, setTab] = useState('status');

  const load = useCallback(async () => {
    try {
      const [a, d, m] = await Promise.all([api.get('/farm/application'), api.get('/farm/documents'), api.get('/farm/messages')]);
      setFarm(a.data); setDocs(d.data); setMessages(m.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);
    try {
      await api.post('/farm/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded securely');
      load();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    try { await api.post('/farm/messages', { body: msgText }); setMsgText(''); load(); } catch { toast.error('Could not send'); }
  };

  const resubmit = async () => {
    try { await api.post('/farm/application/submit'); toast.success('Re-submitted for review'); load(); } catch { toast.error('Failed'); }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (!farm) return <div className="min-h-screen bg-cream flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest" /></div>;

  const meta = STATUS_META[farm.status] || STATUS_META['Submitted'];
  const app = farm.application || {};

  return (
    <div className="min-h-screen bg-cream" data-testid="application-status-page">
      <header className="h-16 bg-white border-b border-forest/10 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
        <div className="flex items-center gap-2"><div className="w-9 h-9 bg-forest rounded-full flex items-center justify-center"><Leaf className="w-5 h-5 text-cream" /></div><span className="font-playfair text-xl font-bold text-dark">FarmBox <span className="text-forest text-sm">Partner</span></span></div>
        <div className="flex items-center gap-3"><NotificationBell /><button onClick={handleLogout} className="flex items-center gap-2 text-dark/60 hover:text-red-600 font-manrope text-sm"><LogOut className="w-4 h-4" />Log out</button></div>
      </header>

      <div className="container-custom py-10 max-w-4xl">
        <div className={`rounded-2xl p-6 md:p-8 ${meta.bg} border border-forest/10 mb-8`} data-testid="status-banner">
          <div className="flex items-center gap-4">
            <meta.icon className={`w-10 h-10 ${meta.color}`} />
            <div>
              <p className="font-manrope text-sm text-dark/50 uppercase tracking-widest font-bold">Application Status</p>
              <h1 className="font-playfair text-3xl font-bold text-dark" data-testid="status-value">{farm.status}</h1>
            </div>
          </div>
          <p className="font-manrope text-dark/70 mt-4">{meta.msg}</p>
          {farm.status_note && <p className="font-manrope text-dark/70 mt-2 bg-white rounded-xl p-3"><strong>Note from FarmBox:</strong> {farm.status_note}</p>}
          {farm.status === 'More Information Needed' && <button onClick={resubmit} className="btn-primary mt-4 py-2.5 px-6 text-sm" data-testid="resubmit-btn">Re-submit Application</button>}
        </div>

        <div className="flex gap-2 mb-6 border-b border-forest/10">
          {['status', 'documents', 'messages'].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 font-manrope font-medium capitalize border-b-2 -mb-px ${tab === t ? 'border-forest text-forest' : 'border-transparent text-dark/50'}`} data-testid={`app-tab-${t}`}>{t}</button>
          ))}
        </div>

        {tab === 'status' && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10" data-testid="application-summary">
            <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Application Summary</h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 font-manrope">
              {[['Farm name', farm.name], ['Contact', farm.contact_name], ['Email', farm.email], ['Phone', farm.phone], ['Town', app.town], ['Availability', app.availability], ['Preferred contact', app.preferred_contact], ['Pricing', app.pricing_option]].map(([k, v]) => (
                <div key={k}><p className="text-dark/50 text-sm">{k}</p><p className="text-dark font-medium">{v || '—'}</p></div>
              ))}
            </div>
            {app.business_types?.length > 0 && <div className="mt-6"><p className="text-dark/50 text-sm font-manrope mb-2">Business types</p><div className="flex flex-wrap gap-2">{app.business_types.map((b) => <span key={b} className="badge-local">{b}</span>)}</div></div>}
            {app.products_available?.length > 0 && <div className="mt-4"><p className="text-dark/50 text-sm font-manrope mb-2">Products</p><div className="flex flex-wrap gap-2">{app.products_available.map((b) => <span key={b} className="badge-organic">{b}</span>)}</div></div>}
            {app.compliance?.length > 0 && <div className="mt-4"><p className="text-dark/50 text-sm font-manrope mb-2">Certifications</p><div className="flex flex-wrap gap-2">{app.compliance.map((b) => <span key={b} className="badge-local">{b}</span>)}</div></div>}
          </div>
        )}

        {tab === 'documents' && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10" data-testid="documents-section">
            <h2 className="font-playfair text-2xl font-bold text-dark mb-2">Compliance Documents</h2>
            <p className="font-manrope text-sm text-dark/60 mb-6">Uploads are stored securely and are never public. FarmBox reviews certifications before displaying anything to customers.</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <select value={docType} onChange={(e) => setDocType(e.target.value)} className="bg-cream border border-forest/20 rounded-xl px-4 py-2.5 font-manrope" data-testid="doc-type-select">{DOC_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              <label className="btn-secondary py-2.5 px-6 text-sm cursor-pointer" data-testid="doc-upload-btn"><Upload className="w-4 h-4 mr-2" />{uploading ? 'Uploading…' : 'Upload Document'}<input type="file" className="hidden" onChange={handleUpload} disabled={uploading} /></label>
            </div>
            <div className="space-y-2">
              {docs.length === 0 ? <p className="font-manrope text-dark/50">No documents uploaded yet.</p> : docs.map((d) => (
                <div key={d.file_id} className="flex items-center gap-3 p-3 bg-cream rounded-xl" data-testid={`doc-${d.file_id}`}>
                  <FileText className="w-5 h-5 text-forest" />
                  <div className="flex-1"><p className="font-manrope text-sm font-medium text-dark">{d.original_filename}</p><p className="font-manrope text-xs text-dark/50">{d.doc_type}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'messages' && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10" data-testid="messages-section">
            <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Messages with FarmBox</h2>
            <div className="space-y-3 max-h-80 overflow-auto mb-4">
              {messages.length === 0 ? <p className="font-manrope text-dark/50">No messages yet.</p> : messages.map((m) => (
                <div key={m.message_id} className={`max-w-[80%] p-3 rounded-2xl font-manrope text-sm ${m.sender_role === 'farm' ? 'ml-auto bg-forest text-cream' : 'bg-cream text-dark'}`}>
                  <p>{m.body}</p><p className="text-[10px] opacity-60 mt-1">{m.sender_name} · {new Date(m.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendMsg()} placeholder="Message FarmBox…" className="flex-1 bg-cream border border-forest/20 rounded-xl px-4 py-2.5 font-manrope" data-testid="message-input" />
              <button onClick={sendMsg} className="btn-primary py-2.5 px-5" data-testid="message-send"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
